"use client";

import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

const WIDTH = 640;
const HEIGHT = 150;
const PAD_X = 28;
const BASE_Y = 84;
const AXIS_Y = 112;
const DOT_R = 5;
const STACK_STEP = 11;
const MAX_STACK = 5;

/**
 * A one-dimensional dot plot: every value on a number line, repeated values stacked,
 * values flagged by at least one method in the accent colour, and the Tukey fences as
 * dashed lines. Colour is never the only cue — flagged dots are also drawn larger with a
 * ring — and the whole chart carries a text description for screen readers.
 */
export function StripPlot({
  data,
  flagged,
  lowerFence,
  upperFence,
  fmt,
  label,
  fenceLabel,
}: {
  data: readonly number[];
  flagged: ReadonlySet<number>;
  lowerFence: number;
  upperFence: number;
  fmt: NumberFormatter;
  label: string;
  fenceLabel: string;
}) {
  const lo = Math.min(...data, lowerFence);
  const hi = Math.max(...data, upperFence);
  const axis = niceTicks(lo, hi, 6);
  const span = axis.hi - axis.lo || 1;
  const x = (value: number) =>
    PAD_X + ((value - axis.lo) / span) * (WIDTH - 2 * PAD_X);
  const seen = new Map<number, number>();
  const dots = data.map((value) => {
    const stack = Math.min(seen.get(value) ?? 0, MAX_STACK);
    seen.set(value, (seen.get(value) ?? 0) + 1);
    return { value, stack, hit: flagged.has(value) };
  });

  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      {[lowerFence, upperFence].map((fence, index) => (
        <line
          key={index === 0 ? "lower" : "upper"}
          stroke={CHART_COLORS[1]}
          strokeDasharray="5 4"
          strokeWidth={1.5}
          x1={x(fence)}
          x2={x(fence)}
          y1={16}
          y2={AXIS_Y}
        />
      ))}
      <text
        fill="currentColor"
        fontSize={11}
        textAnchor="middle"
        x={WIDTH / 2}
        y={HEIGHT - 6}
      >
        {fenceLabel}
      </text>
      {dots.map((dot, index) => (
        <circle
          cx={x(dot.value)}
          cy={BASE_Y - dot.stack * STACK_STEP}
          fill={dot.hit ? CHART_COLORS[3] : CHART_COLORS[0]}
          fillOpacity={dot.hit ? 1 : 0.7}
          // Repeated values share an x position, so the value alone is not a key.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional dots
          key={index}
          r={dot.hit ? DOT_R + 1.5 : DOT_R}
          stroke={dot.hit ? "currentColor" : "none"}
          strokeWidth={dot.hit ? 1.5 : 0}
        />
      ))}
      <line
        stroke="currentColor"
        strokeWidth={1}
        x1={PAD_X}
        x2={WIDTH - PAD_X}
        y1={AXIS_Y}
        y2={AXIS_Y}
      />
      {axis.ticks.map((tick) => (
        <g key={tick}>
          <line
            stroke="currentColor"
            x1={x(tick)}
            x2={x(tick)}
            y1={AXIS_Y}
            y2={AXIS_Y + 5}
          />
          <text
            fill="currentColor"
            fontSize={12}
            textAnchor="middle"
            x={x(tick)}
            y={AXIS_Y + 19}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
    </svg>
  );
}
